import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { BUILD_VERSION, BUILT_AT } from '../buildInfo';

const LOGO_URL = "https://shopkeventer.com/cdn/shop/files/keventer-logo-removebg-preview_260x.png?v=1648031487";
const BG_IMAGE = "https://images.unsplash.com/photo-1768850418251-17480117ac9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxnbGFzcyUyMG1pbGslMjBib3R0bGUlMjBmcmVzaHxlbnwwfHx8fDE3NzM4ODkwMDV8MA&ixlib=rb-4.1.0&q=85";

const Login = () => {
  const navigate = useNavigate();
  const { login, api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Explain an involuntary sign-out (the account was used on another device).
  useEffect(() => {
    const message = localStorage.getItem('auth_message');
    if (message) {
      localStorage.removeItem('auth_message');
      toast.warning(message, { duration: 8000 });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please enter your email or phone, and your password');
      return;
    }
    
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Login successful!');
      // replace, not push: pressing Back after signing in must not walk back
      // onto the login screen.
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/seed-data');
      toast.success(response.data.message);
      setFormData({ email: 'admin@keventer.com', password: 'Admin@123' });
    } catch (error) {
      if (error.response?.data?.message === 'Data already seeded') {
        toast.info('Sample data already exists');
        setFormData({ email: 'admin@keventer.com', password: 'Admin@123' });
      } else {
        toast.error('Failed to seed data');
      }
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Image (hidden on mobile) */}
      <div className="hidden md:flex md:w-1/2 relative">
        <img 
          src={BG_IMAGE} 
          alt="Fresh milk" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900/80 to-emerald-900/60" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <img src={LOGO_URL} alt="Keventer" className="h-12 w-auto object-contain brightness-0 invert" />
          <div>
            <h1 className="text-4xl font-bold text-white mb-4">Keventer Metro</h1>
            <p className="text-lg text-white/80">
              Field Sales Management System for efficient order tracking, visit management, and real-time reporting.
            </p>
          </div>
          <p className="text-sm text-white/60">© 2024 Keventer Agro Limited</p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden mb-8 text-center">
            <img src={LOGO_URL} alt="Keventer" className="h-12 mx-auto mb-4" />
          </div>

          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome Back</h2>
              <p className="text-slate-500">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email or Phone
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="text"
                    inputMode="email"
                    autoComplete="username"
                    placeholder="Email address or phone number"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12 pl-10 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="h-12 pl-10 pr-10 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-medium shadow-lg shadow-sky-200 transition-all active:scale-98"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* <div className="mt-6 pt-6 border-t border-slate-100">
              <button
                onClick={handleSeedData}
                disabled={seeding}
                data-testid="seed-data-btn"
                className="w-full h-10 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium text-sm transition-all flex items-center justify-center gap-2"
              >
                {seeding ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading sample data...
                  </>
                ) : (
                  'Load Sample Data (Demo)'
                )}
              </button>
              <p className="mt-3 text-xs text-center text-slate-500">
                Demo credentials: admin@keventer.com / Admin@123
              </p>
            </div> */}

            {/* Which build this handset is actually running. Without it there is
                no way to tell an updated app from a stale one. */}
            <p className="mt-6 text-center text-[11px] text-slate-400" data-testid="build-stamp">
              Keventer Metro v{BUILD_VERSION}
              {BUILT_AT && (
                <> · {new Date(BUILT_AT).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric'
                })}</>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
